import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Hash, Music, Users, Play, Compass } from "lucide-react";
import { initials } from "@/lib/format";

const CreatorRow = ({ c, onClick }) => (
  <button onClick={onClick} className="flex items-center gap-3 rounded-xl p-2 hover:bg-secondary/60 transition-colors w-full text-left" data-testid={`explore-creator-${c.username}`}>
    <Avatar className="h-11 w-11">
      {c.avatar && <AvatarImage src={c.avatar} alt={c.username} />}
      <AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">{initials(c.username)}</AvatarFallback>
    </Avatar>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium truncate">@{c.username}</p>
      <p className="text-xs text-muted-foreground truncate flex items-center gap-2">
        <span className="flex items-center gap-1"><Play className="h-3 w-3" />{c.video_count}</span>
        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{c.subscriber_count}</span>
      </p>
    </div>
  </button>
);

const Explore = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [explore, setExplore] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounce = useRef();

  useEffect(() => {
    api.get("/explore").then(({ data }) => setExplore(data)).catch(() => setExplore({ suggested_creators: [], trending_hashtags: [], trending_sounds: [] }));
  }, []);

  const runSearch = useCallback(async (term) => {
    if (!term.trim()) { setResults(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/search", { params: { q: term } });
      setResults(data);
    } catch (e) { setResults({ creators: [], hashtags: [], sounds: [] }); }
    finally { setLoading(false); }
  }, []);

  const onChange = (e) => {
    const v = e.target.value;
    setQ(v);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => runSearch(v), 300);
  };

  const goTag = (tag) => navigate(`/decouvrir?tag=${encodeURIComponent(tag)}`);
  const goSound = (s) => navigate(`/son/${encodeURIComponent(s.key || s.sound)}`);
  const goCreator = (u) => navigate(`/c/${u.username}`);

  const SoundRow = ({ s }) => (
    <button onClick={() => goSound(s)} className="flex items-center gap-3 rounded-xl p-2 hover:bg-secondary/60 transition-colors w-full text-left" data-testid={`explore-sound-${s.key}`}>
      <div className="h-11 w-11 rounded-lg overflow-hidden bg-secondary grid place-items-center shrink-0">
        {s.artwork_url ? <img src={s.artwork_url} alt="" className="h-full w-full object-cover" /> : <Music className="h-5 w-5 text-primary" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{s.title || s.sound}</p>
        <p className="text-xs text-muted-foreground truncate">{s.artist || "—"} · {s.count} vidéo(s)</p>
      </div>
    </button>
  );

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl flex items-center gap-2"><Compass className="h-6 w-6 text-primary" /> {t("explore.title")}</h1>

      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-3.5 text-muted-foreground" />
        <Input value={q} onChange={onChange} placeholder={t("explore.searchPlaceholder")} className="pl-9 rounded-xl h-11" data-testid="explore-search-input" />
      </div>

      {results ? (
        <Tabs defaultValue="creators" className="w-full">
          <TabsList className="grid grid-cols-3 rounded-xl">
            <TabsTrigger value="creators" data-testid="explore-tab-creators"><Users className="h-4 w-4 mr-1" /> {t("explore.creators")}</TabsTrigger>
            <TabsTrigger value="hashtags" data-testid="explore-tab-hashtags"><Hash className="h-4 w-4 mr-1" /> {t("explore.hashtags")}</TabsTrigger>
            <TabsTrigger value="sounds" data-testid="explore-tab-sounds"><Music className="h-4 w-4 mr-1" /> {t("explore.sounds")}</TabsTrigger>
          </TabsList>
          <TabsContent value="creators" className="mt-3 space-y-1">
            {loading ? <Skeleton className="h-14 rounded-xl" /> : results.creators.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">{t("explore.noResults")}</p> : results.creators.map((c) => <CreatorRow key={c.id} c={c} onClick={() => goCreator(c)} />)}
          </TabsContent>
          <TabsContent value="hashtags" className="mt-3 flex flex-wrap gap-2">
            {results.hashtags.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8 w-full">{t("explore.noResults")}</p> : results.hashtags.map((h) => (
              <button key={h.tag} onClick={() => goTag(h.tag)} className="flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-sm hover:bg-primary/10 hover:text-primary transition-colors" data-testid={`explore-hashtag-${h.tag}`}>
                <Hash className="h-3.5 w-3.5" />{h.tag}<span className="opacity-60 text-xs">{h.count}</span>
              </button>
            ))}
          </TabsContent>
          <TabsContent value="sounds" className="mt-3 space-y-1">
            {results.sounds.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">{t("explore.noResults")}</p> : results.sounds.map((s) => <SoundRow key={s.key} s={s} />)}
          </TabsContent>
        </Tabs>
      ) : (
        <div className="space-y-6">
          {/* Trending hashtags */}
          <div>
            <h2 className="font-display text-lg mb-2 flex items-center gap-1.5"><Hash className="h-4 w-4 text-primary" /> {t("explore.trendingTags")}</h2>
            {!explore ? <Skeleton className="h-9 rounded-xl" /> : (
              <div className="flex flex-wrap gap-2" data-testid="explore-trending-hashtags">
                {explore.trending_hashtags.length === 0 ? <p className="text-sm text-muted-foreground">—</p> : explore.trending_hashtags.map((h) => (
                  <button key={h.tag} onClick={() => goTag(h.tag)} className="flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-sm hover:bg-primary/10 hover:text-primary transition-colors" data-testid={`explore-trend-tag-${h.tag}`}>
                    <Hash className="h-3.5 w-3.5" />{h.tag}<span className="opacity-60 text-xs">{h.count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Suggested creators */}
          <div>
            <h2 className="font-display text-lg mb-2 flex items-center gap-1.5"><Users className="h-4 w-4 text-primary" /> {t("explore.suggested")}</h2>
            {!explore ? <div className="space-y-2">{[0,1,2].map(i => <Skeleton key={i} className="h-14 rounded-xl" />)}</div> : (
              <div className="space-y-1" data-testid="explore-suggested-creators">
                {explore.suggested_creators.length === 0 ? <p className="text-sm text-muted-foreground">—</p> : explore.suggested_creators.map((c) => <CreatorRow key={c.id} c={c} onClick={() => goCreator(c)} />)}
              </div>
            )}
          </div>

          {/* Trending sounds */}
          <div>
            <h2 className="font-display text-lg mb-2 flex items-center gap-1.5"><Music className="h-4 w-4 text-primary" /> {t("explore.trendingSounds")}</h2>
            {!explore ? <Skeleton className="h-14 rounded-xl" /> : (
              <div className="space-y-1" data-testid="explore-trending-sounds">
                {explore.trending_sounds.length === 0 ? <p className="text-sm text-muted-foreground">—</p> : explore.trending_sounds.map((s) => <SoundRow key={s.key} s={s} />)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Explore;
